import { Text } from "@/components/ui/text";
import { DataGuard } from "@components/DataGuard";
import { PageSearch, useSearchParamSync } from "@components/PageSearch";
import Panel from "@components/Panel";
import { pill } from "@features/communityContent/topics";
import { useGlobalSearch } from "@features/GlobalSearch";
import { For, Show, createSignal } from "solid-js";
import { useService } from "solid-services";
import { css } from "styled-system/css";
import { Box, Flex, Grid } from "styled-system/jsx";
import { GROUPS, getCourseDecor } from "../courseDecor";
import { useCourses } from "../useCourses";
import Card from "./Card";
import Intro from "./Intro";

type Progress = 'all' | 'not-started' | 'in-progress' | 'completed';

const PROGRESS: { value: Progress; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'not-started', label: 'Not started' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
];

const progressOf = (c: { completed_lessons: number; total_lessons: number }): Progress => {
  const done = Number(c.completed_lessons || 0);
  const total = Number(c.total_lessons || 0);
  if (done === 0) return 'not-started';
  if (total > 0 && done >= total) return 'completed';
  return 'in-progress';
};

export default function () {
  const { coursesQuery } = useService(useCourses)();
  const { open: openGlobalSearch } = useService(useGlobalSearch)();
  const [search, setSearch] = createSignal('');
  const [progress, setProgress] = createSignal<Progress>('all');

  useSearchParamSync(search, setSearch);

  const matches = (c: { title: string; description: string; completed_lessons: number; total_lessons: number }) => {
    const q = search().trim().toLowerCase();
    if (q && !`${c.title} ${c.description ?? ''}`.toLowerCase().includes(q)) return false;
    return progress() === 'all' || progressOf(c) === progress();
  };

  return (
    <>
      <Intro />
      <Panel
        display="block"
        height="calc(var(--vh) * 100 - 70px)"
        overflowY="auto"
        px={{ base: '22px', smDown: '12px' }}
        py={{ base: '24px', smDown: '14px' }}
      >
        <DataGuard query={coursesQuery}>
          {(courses) => (
            <Box maxWidth="1080px" mx="auto">
              <Flex gap="10px" alignItems="center" flexWrap="wrap" mb="24px">
                <PageSearch
                  value={search()}
                  onInput={setSearch}
                  placeholder="Search courses…"
                  class={css({ w: { base: '240px', smDown: 'full' } })}
                />
                <Flex role="group" aria-label="Filter by progress" gap="7px" flexWrap="wrap">
                  <For each={PROGRESS}>
                    {(p) => (
                      <button type="button" class={pill(progress() === p.value)} onClick={() => setProgress(p.value)}>
                        {p.label}
                      </button>
                    )}
                  </For>
                </Flex>
              </Flex>

              {/* Lessons aren't listed on this page, so hand lesson searches to global search */}
              <Show when={search().trim().length >= 2}>
                <Text fontSize="12px" color="editorialInk3" mb="18px">
                  Looking for a specific lesson?{' '}
                  <button
                    type="button"
                    class={css({ color: 'jade.11', cursor: 'pointer', textDecoration: 'underline' })}
                    onClick={() => openGlobalSearch({ category: 'course', query: search().trim() })}
                  >
                    Search lessons for “{search().trim()}”
                  </button>
                </Text>
              </Show>

              <Show when={courses.filter(matches).length === 0}>
                <Text color="fg.muted" textAlign="center" py="8">
                  No courses match.
                </Text>
              </Show>

              <For each={GROUPS}>
                {(group) => {
                  const rows = () =>
                    courses
                      .filter((c) => getCourseDecor(c.title).group === group.key && matches(c))
                      .sort((a, b) => getCourseDecor(a.title).rank - getCourseDecor(b.title).rank);

                  return (
                    <Box display={rows().length ? 'block' : 'none'} mb="38px">
                      <Flex
                        align="baseline"
                        gap="10px"
                        mb="13px"
                        px="2px"
                        flexWrap="wrap"
                      >
                        <Box
                          fontSize="11px"
                          fontWeight="700"
                          letterSpacing=".07em"
                          textTransform="uppercase"
                          color="editorialInk2"
                        >
                          {group.label}
                        </Box>
                        <Box fontSize="12px" color="editorialInk3">
                          {group.sub}
                        </Box>
                      </Flex>

                      <Grid gap="11px">
                        <For each={rows()}>
                          {(course) => <Card course={course} />}
                        </For>
                      </Grid>
                    </Box>
                  );
                }}
              </For>
            </Box>
          )}
        </DataGuard>
      </Panel>
    </>
  )
}
